/**
 * Stub Engine Service — no-op implementation for when no Engine is connected.
 *
 * Used by both Editor and Web as the default/fallback EngineService.
 * Pass a `prefix` to customize serverInfo.name and sessionId prefix.
 */

import type { EngineService } from "../platform";
import { t as translate } from "@magic-md/i18n";

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

export function createStubEngineService(prefix = "stub"): EngineService {
    return {
        async getEngineStatus() {
            return { alive: false, ready: false };
        },
        async initialize() {
            return {
                serverInfo: { name: prefix, version: "0.0.0" },
                plugins: [],
                capabilities: { fileWatch: false, cascadeExecution: false, protocolVersion: "0.0.0" },
            };
        },
        async sessionCreate() {
            return { sessionId: `${prefix}-${Date.now()}`, units: [], executionOrder: [] };
        },
        async sessionClose() {
            return { success: true };
        },
        async sessionSync() {
            return { units: [], executionOrder: [], changedUnitIds: [] };
        },
        async sessionList() {
            return { sessions: [] };
        },
        async sessionGetState() {
            return { sessionId: "", units: [], executionOrder: [], outputs: {} };
        },
        async sessionGetOutput() {
            return {};
        },
        async sessionExplain(params) {
            return {
                mode: params.targetId ? "target" as const : "system" as const,
                sessionId: params.sessionId,
                targetId: params.targetId,
                missingTarget: !!params.targetId,
                summary: {
                    title: params.targetId
                        ? tr("engineStub.explainCannotExplain", { targetId: params.targetId })
                        : tr("engineStub.offlineTitle"),
                    subtitle: tr("engineStub.noRuntimeSession"),
                    status: "error" as const,
                },
                stats: {
                    unitCount: 0,
                    definitionCount: 0,
                    forCount: 0,
                    contentCount: 0,
                    importCount: 0,
                    useCount: 0,
                    outputCount: 0,
                    parseDiagnosticCount: 0,
                },
                executionOrder: [],
                diagnostics: [
                    {
                        level: "error" as const,
                        message: tr("engineStub.explainUnavailable"),
                        code: "engine-offline",
                    },
                ],
                graph: { nodes: [], edges: [] },
            };
        },
        async sessionWatch() {
            return { success: false };
        },
        async sessionUnwatch() {
            return { success: false };
        },
        async executionStart(params) {
            const id = `${prefix}-execution-${Date.now()}`;
            return {
                executionId: id,
                traceId: `${prefix}-trace-${Date.now()}`,
                sessionId: params.sessionId,
                target: params.target,
                status: "running" as const,
                unitId: params.unitId,
                action: params.action,
            };
        },
        async executionGetState(params) {
            return {
                executionId: params.executionId,
                traceId: `${prefix}-trace-unavailable`,
                sessionId: "",
                target: "unit" as const,
                status: "failed" as const,
                startedAt: new Date().toISOString(),
                completedAt: new Date().toISOString(),
                success: false,
                error: tr("engineStub.configureEngine"),
            };
        },
        async executionCancel() {
            return { success: true };
        },
        async jobList() {
            return { jobs: [] };
        },
        async jobGetState(params) {
            return {
                jobId: params.jobId,
                kind: "unknown",
                status: "failed" as const,
                phase: "failed",
                startedAt: new Date().toISOString(),
                completedAt: new Date().toISOString(),
                error: tr("engineStub.configureEngine"),
            };
        },
        async jobCancel() {
            return { success: false };
        },
        async pluginList() {
            return { plugins: [] };
        },
        async pluginApiCall() {
            return { success: false, message: "Engine not connected" };
        },
        async smartIntakePropose() {
            return { offers: [] };
        },
        async smartIntakeBuildPlan() {
            return { plan: { operations: [] } };
        },
        async configGet() {
            return { view: { values: {} }, meta: { entries: {} }, diagnostics: [] };
        },
        async configSet() {
            return { success: false, changedKeys: [] };
        },
        async configGetSchema() {
            return { entries: [] };
        },
        async configReload() {
            return { success: false, changedKeys: [] };
        },
        async sidecarList() {
            return { sidecars: [] };
        },
        async sidecarShutdown() {
            return { success: false };
        },
        async sidecarSpawn(sidecarId) {
            return { success: false, sidecarId, alive: false };
        },
        async sidecarCall() {
            return { success: false, error: "Not connected", durationMs: 0 };
        },
        async sidecarCallStart(params) {
            return {
                jobId: `${prefix}-sidecar-${Date.now()}`,
                sidecarId: params.sidecarId,
                method: params.method,
                status: "running" as const,
            };
        },
        async listenNotifications() {
            return () => { };
        },
        stopListening() { },

        // ── Push ──
        async pushScan() {
            return { devices: [] };
        },
        async pushConnect() {
            return { connected: false };
        },
        async pushDisconnect() {
            return { disconnected: false };
        },
        async pushPair() {
            return { success: false };
        },
        async pushSend() {
            return { received: false };
        },
        async pushSyncStart() {
            return { started: false };
        },
        async pushSyncStop() {
            return { stopped: false };
        },
        async pushStatus() {
            return { connected: false, paired: false, syncing: false };
        },

        // ── MCP ──
        async mcpStart() {
            return { success: false, endpoint: "", port: 0 };
        },
        async mcpStop() {
            return { success: false };
        },
        async mcpStatus() {
            return { status: "stopped", endpoint: null, port: null, connectedClients: 0, clients: [] };
        },
        async mcpLogs() {
            return { logs: [], total: 0 };
        },

        // ── Extension ──
        async extensionList() {
            return { extensions: [] };
        },
        async extensionGetManifest() {
            return { manifest: {} };
        },
        async extensionActivateRuntime(params) {
            return { extensionId: params.extensionId, activated: false };
        },
        async extensionInvoke() {
            return { success: false, message: "Engine not connected" };
        },
        async extensionDisposeRuntime(params) {
            return { extensionId: params.extensionId, disposed: false };
        },
        async extensionSettingsUpdate(params) {
            return { extensionId: params.extensionId, applied: false };
        },

        // ── Search ──
        async searchQuery() {
            return { searchId: "" };
        },
        async searchCancel() {
            return { success: false };
        },

        // ── Workspace ──
        async workspaceReadDir() {
            return { entries: [] };
        },
        async workspaceReadFile() {
            return { content: "" };
        },
        async workspaceWriteFile() {
            return { success: false };
        },
        async workspaceStat() {
            return { size: 0, mtime: null, isFile: true, isDirectory: false };
        },
        async workspaceExists() {
            return { exists: false };
        },
        async workspaceMkdir() {
            return { success: false };
        },
        async workspaceRename() {
            return { success: false, actualPath: "" };
        },
        async workspaceRemove() {
            return { success: false };
        },
        async workspaceCopy() {
            return { success: false, actualPath: "" };
        },
        async workspaceMove() {
            return { results: [] };
        },
        async workspaceReferenceImpact() {
            return {
                workspaceActive: false,
                scannedFiles: 0,
                references: [],
                warnings: [],
                canAutoApply: true,
            };
        },
        async workspaceGetInfo() {
            return {
                folders: [],
                allowedRoots: [],
                sep: "/",
                os: "unknown",
                home: "",
            };
        },
        async workspaceAddFolder() {
            return { success: false, folder: { path: "", name: "" } };
        },
        async workspaceRemoveFolder() {
            return { success: false };
        },
        async workspaceListFolders() {
            return { folders: [] };
        },
        async workspaceSave() {
            return { success: false };
        },
        async workspaceLoad() {
            return { success: false, folders: [], starred: [], bookmarks: [], session: null };
        },
        async workspaceStarList() {
            return { items: [] };
        },
        async workspaceStarAdd() {
            return { success: false, item: { path: "", kind: "file", addedAt: 0 } };
        },
        async workspaceStarRemove() {
            return { success: false };
        },
        async workspaceStarUpdate() {
            return { success: false, item: null };
        },
        async workspaceBookmarkList() {
            return { items: [] };
        },
        async workspaceBookmarkAdd() {
            return {
                success: false,
                item: {
                    id: "",
                    path: "",
                    line: 1,
                    createdAt: 0,
                    updatedAt: 0,
                },
            };
        },
        async workspaceBookmarkRemove() {
            return { success: false };
        },
        async workspaceBookmarkUpdate() {
            return { success: false, item: null };
        },
        async workspaceSessionGet() {
            return { session: null };
        },
        async workspaceSessionSet() {
            return { success: false };
        },

        // ── Intelligence (empty defaults when Engine is offline) ──
        async intelligenceComplete() { return { items: [] }; },
        async intelligenceHover() { return null; },
        async intelligenceDiagnostics() { return { diagnostics: [] }; },
        async intelligenceDefinition() { return null; },
        async intelligenceReferences() { return { references: [] }; },
        async intelligenceRename() { return { newContent: "", changes: 0 }; },
        async intelligenceSymbols() { return { symbols: [] }; },
        async intelligenceSemanticTokens() { return { tokens: [] }; },
        async intelligenceCodeAction() { return { actions: [] }; },
        async intelligenceInlayHint() { return { hints: [] }; },

        // ── Publish ──
        async publishStart() { return { taskId: "" }; },
        async publishGetState() { return { taskId: "", status: "failed" as const, error: "Engine offline" }; },
        async publishCancel() { return { success: false }; },
        async publishSiteStart() { return { taskId: "" }; },
        async publishSiteGetState() { return { taskId: "", status: "failed" as const, error: "Engine offline" }; },
        async publishSiteCancel() { return { success: false }; },

        // ── History (Snapshots) ──
        async historySnapshot() { return { created: false, skipped: "disabled" as const }; },
        async historyList() { return { snapshots: [], total: 0 }; },
        async historyGet() {
            throw new Error("history/get unavailable: Engine offline");
        },
        async historyDiff() { return { unifiedDiff: "" }; },
        async historyRestore() {
            throw new Error("history/restore unavailable: Engine offline");
        },
        async historyDelete() { return { removed: false }; },
        async historyTag() {
            throw new Error("history/tag unavailable: Engine offline");
        },
        async historyPrune() { return { removed: [], kept: [] }; },
    };
}
